/**
 * ClassSelector - Dropdown for selecting a class with optional create new
 */

import { useState, useEffect, useMemo } from 'react';
import { Class, ClassWithMetrics } from '@/types/class';
import { classesService } from '@/services/classes';
import { Badge } from '@/components/ui/badge';

interface ClassSelectorProps {
  value: string;
  onChange: (classId: string) => void;
  onCreateNew?: () => void;
  error?: string;
  reloadToken?: number;
  optimisticClass?: Class | null;
}

export function ClassSelector({
  value,
  onChange,
  onCreateNew,
  error,
  reloadToken = 0,
  optimisticClass = null,
}: ClassSelectorProps) {
  const [classes, setClasses] = useState<ClassWithMetrics[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isActive = true;

    async function loadClasses() {
      try {
        setIsLoading(true);
        setLoadError('');
        const fetchedClasses = await classesService.listClasses();
        if (isActive) {
          setClasses(fetchedClasses);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load classes';
        if (isActive) {
          setLoadError(message);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    }

    void loadClasses();

    return () => {
      isActive = false;
    };
  }, [reloadToken]);

  const classOptions = useMemo(() => {
    if (!optimisticClass) {
      return classes;
    }

    const exists = classes.some((classItem) => classItem.classId === optimisticClass.classId);
    if (exists) {
      return classes;
    }

    return [{ ...optimisticClass, sessionCount: 0 }, ...classes];
  }, [classes, optimisticClass]);

  const selectedClass = classOptions.find((c) => c.classId === value);

  return (
    <div className="space-y-2">
      <label htmlFor="class-selector" className="block text-sm font-medium text-slate-700">
        Select Class <span className="text-red-500">*</span>
      </label>

      {isLoading ? (
        <div className="text-sm text-slate-500">Loading classes...</div>
      ) : loadError ? (
        <div className="text-sm text-red-600">{loadError}</div>
      ) : (
        <select
          id="class-selector"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#12356B]/25 focus:border-[#12356B] ${
            error ? 'border-red-500' : 'border-[#CED4DE]'
          }`}
          required
        >
          <option value="">-- Select a class --</option>
          {classOptions.map((classItem) => (
            <option key={classItem.classId} value={classItem.classId}>
              {classItem.className} ({classItem.sessionCount} sessions)
            </option>
          ))}
          {onCreateNew && (
            <option value="__create_new__">+ Create New Class</option>
          )}
        </select>
      )}

      {/* Selected class display */}
      {selectedClass && (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Badge variant="secondary" className="bg-[#E2E5EC] text-[#12356B]">
            {selectedClass.sessionCount} existing sessions
          </Badge>
          {selectedClass.description && (
            <span className="text-xs truncate">{selectedClass.description}</span>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}

      {/* Create new button (alternative to dropdown option) */}
      {onCreateNew && !isLoading && classOptions.length === 0 && (
        <button
          type="button"
          onClick={onCreateNew}
          className="text-sm text-[#12356B] hover:text-[#0d2850] font-medium"
        >
          + Create your first class
        </button>
      )}
    </div>
  );
}
